import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Html5Qrcode } from 'html5-qrcode';
import './QuickPackPage.css';

// Helper to play a crisp positive "Success" chime using Web Audio API
const playSuccessSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;
    osc.type = 'sine';

    // Note 1: C5
    osc.frequency.setValueAtTime(523.25, now);
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    // Note 2: G5
    osc.frequency.setValueAtTime(783.99, now + 0.08);
    gain.gain.setValueAtTime(0.15, now + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

    osc.start(now);
    osc.stop(now + 0.22);
  } catch (err) {
    console.warn('Web Audio synthesis failed:', err);
  }
};

// Helper to play a short notification chime when an order is loaded or scanned
const playLoadedSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;
    osc.type = 'sine';

    osc.frequency.setValueAtTime(659.25, now); // E5
    gain.gain.setValueAtTime(0.1, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    osc.start(now);
    osc.stop(now + 0.08);
  } catch (err) {
    console.warn('Web Audio synthesis failed:', err);
  }
};

// Helper to play a low buzzer sound for "Error/Warning"
const playErrorSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;
    osc.type = 'sawtooth';

    osc.frequency.setValueAtTime(140, now);
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

    osc.start(now);
    osc.stop(now + 0.35);
  } catch (err) {
    console.warn('Web Audio synthesis failed:', err);
  }
};

const DEFAULT_PRESETS = ['BlueDart', 'ExpressBees', 'DTDC', 'Ecom Express', 'Amazon Shipping', 'Smartr'];

function QuickPackPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Inputs & focus refs
  const orderInputRef = useRef(null);
  const awbInputRef = useRef(null);
  const scannerInstanceRef = useRef(null);

  // Core packing states
  const [orderQuery, setOrderQuery] = useState('');
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [awb, setAwb] = useState('');
  
  // Courier partner states
  const [shipmentPartner, setShipmentPartner] = useState('delhivery'); // 'delhivery' | 'shiprocket' | 'other'
  const [customCourierName, setCustomCourierName] = useState('');
  const [previousPartners, setPreviousPartners] = useState([]);

  // Camera Barcode Scanner State
  const [activeScanner, setActiveScanner] = useState(null); // 'order' | 'awb' | null

  // Status/Feedback states
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  // Local history of packed items (session)
  const [recentPacks, setRecentPacks] = useState(() => {
    try {
      const saved = localStorage.getItem('quickPackRecentHistory');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Load unique custom couriers list on mount
  useEffect(() => {
    try {
      const savedCustom = localStorage.getItem('quickPackCustomPartners');
      const loaded = savedCustom ? JSON.parse(savedCustom) : [];
      // Combine with defaults and filter out duplicates
      const unique = Array.from(new Set([...DEFAULT_PRESETS, ...loaded].map(x => String(x || '').trim()).filter(Boolean)));
      setPreviousPartners(unique);
    } catch (e) {
      setPreviousPartners(DEFAULT_PRESETS);
    }
  }, []);

  // Focus order input on load
  useEffect(() => {
    if (orderInputRef.current && !activeScanner) {
      orderInputRef.current.focus();
    }
  }, [activeScanner]);

  // Save successful pack in history
  const saveRecentPack = (item) => {
    setRecentPacks((prev) => {
      const next = [item, ...prev].slice(0, 5); // Keep last 5
      try {
        localStorage.setItem('quickPackRecentHistory', JSON.stringify(next));
      } catch (e) {
        console.warn('Failed to save quick pack history:', e);
      }
      return next;
    });
  };

  // Helper for authenticated API calls
  const authedFetch = useCallback(async (url, init = {}) => {
    if (!currentUser) throw new Error('You must be logged in.');
    const token = await currentUser.getIdToken();
    const headers = {
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    };
    if (init.body != null && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(url, { ...init, headers });
  }, [currentUser]);

  // Direct order search implementation
  const triggerOrderSearchDirect = useCallback(async (queryText) => {
    const query = String(queryText || '').trim();
    if (!query) return;

    setIsLoadingOrder(true);
    setErrorMsg('');
    setSuccessMsg('');
    setOrder(null);
    setLines([]);
    setAwb('');

    try {
      // Find matching orders
      const response = await authedFetch(`/api/v9/orders?search=${encodeURIComponent(query)}&searchField=orderNumber`);
      const data = await response.json().catch(() => ({}));
      
      if (!response.ok || !data?.ok) {
        throw new Error(data?.error?.message || 'Failed to search order.');
      }

      const foundOrders = data?.result?.items || [];
      if (foundOrders.length === 0) {
        throw new Error(`No order found matching "${query}".`);
      }

      // Exact/first match
      const matchedOrder = foundOrders[0];

      // Retrieve lines (check inline first, else fetch details)
      let orderLines = Array.isArray(matchedOrder?.lines) ? matchedOrder.lines : [];
      if (orderLines.length === 0) {
        const detailRes = await authedFetch(`/api/v9/orders/${encodeURIComponent(matchedOrder.id)}`);
        const detailData = await detailRes.json().catch(() => ({}));
        if (!detailRes.ok || !detailData?.ok) {
          throw new Error(detailData?.error?.message || 'Failed to load order item details.');
        }
        orderLines = Array.isArray(detailData?.result?.lines) ? detailData.result.lines : [];
      }

      if (orderLines.length === 0) {
        throw new Error(`No items found in order #${query}.`);
      }

      // Success loading order
      setOrder(matchedOrder);
      setLines(orderLines);
      
      // Pre-fill tracking AWB if it already exists in the system
      const existingAwb = String(matchedOrder?.shipment?.awb || matchedOrder?.prefillAwb || '').trim();
      setAwb(existingAwb);
      
      // Auto-prefill partner if detectable, or keep default
      const prefill = String(matchedOrder?.shipment?.partner || matchedOrder?.prefillPartner || '').trim().toLowerCase();
      if (prefill === 'delhivery' || prefill === 'shiprocket') {
        setShipmentPartner(prefill);
      } else if (prefill) {
        setShipmentPartner('other');
        setCustomCourierName(prefill);
      }

      playLoadedSound();

      // Focus AWB field next
      setTimeout(() => {
        if (awbInputRef.current) {
          awbInputRef.current.focus();
        }
      }, 80);

    } catch (err) {
      playErrorSound();
      setErrorMsg(err.message);
      // Select input text for easy re-scanning
      if (orderInputRef.current) {
        orderInputRef.current.focus();
        orderInputRef.current.select();
      }
    } finally {
      setIsLoadingOrder(false);
    }
  }, [authedFetch]);

  // Handle order search submit (from manual gun / input form)
  const handleOrderSearch = (e) => {
    if (e) e.preventDefault();
    triggerOrderSearchDirect(orderQuery);
  };

  // Helper to get formatted Order Reference
  const getOrderReference = (o) => {
    return o?.sourceLinks?.shopify?.orderName || o?.sourceLinks?.zoho?.referenceNumber || o?.externalOrderId || o?.id || '';
  };

  // Helper to get customer name
  const getCustomerName = (o) => {
    return o?.sourceLinks?.shopify?.customerName || o?.sourceLinks?.zoho?.customerName || o?.shippingAddress?.name || 'Customer';
  };

  // Direct AWB packing submit implementation
  const triggerAwbSubmitDirect = useCallback(async (awbText) => {
    const cleanAwb = String(awbText || '').trim();
    if (!cleanAwb) return;

    if (!order) {
      playErrorSound();
      setErrorMsg('Please scan or enter an Order Number first.');
      if (orderInputRef.current) {
        orderInputRef.current.focus();
      }
      return;
    }

    // Determine final courier name to send
    let finalPartner = shipmentPartner.toLowerCase();
    if (shipmentPartner === 'other') {
      finalPartner = String(customCourierName || '').trim();
      if (!finalPartner) {
        playErrorSound();
        setErrorMsg('Please type or select a custom Courier Partner.');
        return;
      }
    }

    setIsSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const orderId = order.id;
      const orderNumber = getOrderReference(order);

      // Map line items to format required by packing API
      const packLineItems = lines
        .map((line) => ({
          sku: String(line?.sku || '').trim().toUpperCase(),
          qty: Number(line?.netRequiredQty || line?.qty || 0),
        }))
        .filter((li) => li.sku && li.qty > 0);

      if (packLineItems.length === 0) {
        throw new Error('No valid packable items found in this order.');
      }

      // Hit API
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/pack`, {
        method: 'POST',
        body: JSON.stringify({
          shipmentPartner: finalPartner,
          awb: cleanAwb,
          packages: [{
            packageRef: `PKG-${Date.now()}`,
            lineItems: packLineItems,
          }],
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error?.message || 'Failed to submit packing data.');
      }

      // Success
      playSuccessSound();
      setSuccessMsg(`Order #${orderNumber} marked Packed successfully!`);
      saveRecentPack({
        orderNumber,
        awb: cleanAwb,
        partner: finalPartner,
        time: new Date().toLocaleTimeString(),
      });

      // Save custom courier in localStorage unique list if it was new
      if (shipmentPartner === 'other') {
        const cleanedName = String(customCourierName || '').trim();
        if (cleanedName) {
          setPreviousPartners((prev) => {
            const currentList = prev.filter(x => x.toLowerCase() !== cleanedName.toLowerCase());
            const updated = [cleanedName, ...currentList];
            try {
              localStorage.setItem('quickPackCustomPartners', JSON.stringify(updated.filter(x => !DEFAULT_PRESETS.includes(x))));
            } catch (e) {
              console.warn(e);
            }
            return updated;
          });
        }
      }

      // Clear states & reset inputs
      setOrder(null);
      setLines([]);
      setAwb('');
      setOrderQuery('');

      // Focus back to order scanner
      setTimeout(() => {
        if (orderInputRef.current) {
          orderInputRef.current.focus();
        }
      }, 80);

    } catch (err) {
      playErrorSound();
      setErrorMsg(err.message);
      // Select AWB input for easy re-scanning
      if (awbInputRef.current) {
        awbInputRef.current.focus();
        awbInputRef.current.select();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [order, lines, shipmentPartner, customCourierName, authedFetch]);

  // Handle AWB manual submit form
  const handleAwbSubmit = (e) => {
    if (e) e.preventDefault();
    triggerAwbSubmitDirect(awb);
  };

  // Auto-detect shipping partner if 12-digit numeric (Delhivery)
  const handleAwbChange = (e) => {
    const val = e.target.value;
    setAwb(val);
    if (/^\d{12}$/.test(String(val).trim())) {
      setShipmentPartner('delhivery');
    }
  };

  // Scanner Stop Handler
  const stopScanner = useCallback(async () => {
    if (scannerInstanceRef.current) {
      try {
        if (scannerInstanceRef.current.isScanning) {
          await scannerInstanceRef.current.stop();
        }
      } catch (err) {
        console.warn('Error stopping scanner:', err);
      } finally {
        scannerInstanceRef.current = null;
      }
    }
  }, []);

  // Handle successful camera scan
  const handleScanSuccess = useCallback(async (decodedText) => {
    const text = String(decodedText || '').trim();
    if (!text) return;

    playLoadedSound();
    setActiveScanner(null);
    await stopScanner();

    if (activeScanner === 'order') {
      setOrderQuery(text);
      triggerOrderSearchDirect(text);
    } else if (activeScanner === 'awb') {
      setAwb(text);
      // Auto-detect Delhivery
      if (/^\d{12}$/.test(text)) {
        setShipmentPartner('delhivery');
      }
      // Auto-submit AWB pack
      setIsSubmitting(true);
      setTimeout(() => {
        // Run submission on next tick to ensure state has latest data
        triggerAwbSubmitDirect(text);
      }, 100);
    }
  }, [activeScanner, stopScanner, triggerOrderSearchDirect, triggerAwbSubmitDirect]);

  // Initialize and stop the HTML5 QR/Barcode Code Reader
  useEffect(() => {
    if (!activeScanner) {
      stopScanner();
      return;
    }

    const startScanner = async () => {
      try {
        await stopScanner(); // clear any existing instance
        
        // Wait a brief moment to ensure container element is rendered in DOM
        await new Promise((resolve) => setTimeout(resolve, 150));
        
        const container = document.getElementById('quick-pack-camera-reader');
        if (!container) return;

        const html5QrCode = new Html5Qrcode('quick-pack-camera-reader');
        scannerInstanceRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' }, // Back camera
          {
            fps: 15,
            qrbox: (width, height) => {
              const minSize = Math.min(width, height);
              // Make scanner box rectangular to capture wide barcodes easily
              return {
                width: Math.floor(width * 0.85),
                height: Math.max(100, Math.floor(minSize * 0.4)),
              };
            },
          },
          (decodedText) => {
            handleScanSuccess(decodedText);
          },
          () => {
            // Silence warning check beeps to avoid log flooding
          }
        );
      } catch (err) {
        console.error('Html5Qrcode error:', err);
        setErrorMsg('Failed to open camera scanner. Please check device camera permissions.');
        setActiveScanner(null);
      }
    };

    startScanner();

    return () => {
      stopScanner();
    };
  }, [activeScanner, handleScanSuccess, stopScanner]);

  const handleReset = () => {
    setOrder(null);
    setLines([]);
    setAwb('');
    setOrderQuery('');
    setErrorMsg('');
    setSuccessMsg('');
    setCustomCourierName('');
    if (orderInputRef.current) {
      orderInputRef.current.focus();
    }
  };

  return (
    <div className="quick-pack-container">
      {/* Mini Minimal Top Header */}
      <div className="quick-pack-header">
        <button className="back-btn" onClick={() => navigate('/stock-house')}>
          ✕ Exit
        </button>
        <span className="page-title">⚡ Quick Pack (Mobile)</span>
        <button className="reset-btn" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* Main Alert Banners */}
      {errorMsg && <div className="alert alert-error">{errorMsg}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {/* Input Scanner Form */}
      <div className="scanner-section">
        {/* Step 1: Scan Order Number */}
        <form onSubmit={handleOrderSearch} className="scan-form-group">
          <label className="input-label">1. SCAN ORDER LABEL</label>
          <div className="input-with-button">
            <input
              type="text"
              ref={orderInputRef}
              className={`scanner-input ${order ? 'input-complete' : ''}`}
              placeholder="Scan/Enter Shopify/Zoho Order Name..."
              value={orderQuery}
              onChange={(e) => setOrderQuery(e.target.value)}
              disabled={isLoadingOrder || isSubmitting}
              autoComplete="off"
            />
            {isLoadingOrder && <span className="loading-spinner"></span>}
            <button
              type="button"
              className="camera-scan-btn"
              onClick={() => setActiveScanner('order')}
              disabled={isLoadingOrder || isSubmitting}
              title="Scan with Camera"
            >
              📷 Scan
            </button>
          </div>
        </form>

        {/* Step 2: Scan AWB / Tracking Code */}
        <form onSubmit={handleAwbSubmit} className="scan-form-group">
          <label className="input-label">2. SCAN AWB (TRACKING LABEL)</label>
          <div className="input-with-button">
            <input
              type="text"
              ref={awbInputRef}
              className={`scanner-input ${!order ? 'input-locked' : ''}`}
              placeholder={order ? 'Scan/Enter AWB number...' : 'Locked - Scan Order first'}
              value={awb}
              onChange={handleAwbChange}
              disabled={!order || isSubmitting}
              autoComplete="off"
            />
            <button
              type="button"
              className="camera-scan-btn"
              onClick={() => order && setActiveScanner('awb')}
              disabled={!order || isSubmitting}
              title="Scan with Camera"
            >
              📷 Scan
            </button>
          </div>
        </form>
      </div>

      {/* Delivery Partner Selector (Big touch options + OTHER Custom option) */}
      <div className={`partner-selector ${!order ? 'selector-locked' : ''}`}>
        <label className="input-label">COURIER PARTNER</label>
        <div className="partner-options">
          <button
            type="button"
            className={`partner-btn delhivery-btn ${shipmentPartner === 'delhivery' ? 'active' : ''}`}
            onClick={() => order && setShipmentPartner('delhivery')}
            disabled={!order}
          >
            DELHIVERY
          </button>
          <button
            type="button"
            className={`partner-btn shiprocket-btn ${shipmentPartner === 'shiprocket' ? 'active' : ''}`}
            onClick={() => order && setShipmentPartner('shiprocket')}
            disabled={!order}
          >
            SHIPROCKET
          </button>
          <button
            type="button"
            className={`partner-btn other-btn ${shipmentPartner === 'other' ? 'active' : ''}`}
            onClick={() => order && setShipmentPartner('other')}
            disabled={!order}
          >
            OTHER
          </button>
        </div>
      </div>

      {/* Manual Custom Partner Dropdown/Datalist entry */}
      {shipmentPartner === 'other' && order && (
        <div className="custom-partner-section">
          <label className="input-label">ENTER COURIER PARTNER NAME</label>
          <input
            type="text"
            className="scanner-input custom-partner-input"
            placeholder="Type or select courier partner..."
            value={customCourierName}
            onChange={(e) => setCustomCourierName(e.target.value)}
            list="previous-partners-list"
            disabled={isSubmitting}
            autoComplete="off"
          />
          <datalist id="previous-partners-list">
            {previousPartners.map((partner) => (
              <option key={partner} value={partner} />
            ))}
          </datalist>
        </div>
      )}

      {/* Loaded Order Verification Card */}
      {order && (() => {
        const orderStatus = String(order?.orderStatus || '').toUpperCase();
        const isPacked = ['PACKED', 'SHIPPED', 'CLOSED'].includes(orderStatus);
        
        return (
          <div className="order-details-card">
            <div className="order-card-header">
              <div>
                <span className="order-name">#{getOrderReference(order)}</span>
                <span className={`status-badge ${isPacked ? 'packed' : 'ready'}`}>
                  {isPacked ? orderStatus : 'READY TO PACK'}
                </span>
              </div>
              <div className="customer-info">
                {getCustomerName(order)} ({order?.shippingAddress?.city || 'No City'})
              </div>
              {order?.shipment?.awb && (
                <div className="existing-tracking-info">
                  Existing AWB: <strong>{order.shipment.awb}</strong> ({String(order.shipment.partner || '').toUpperCase()})
                </div>
              )}
              {order?.shipment?.reportedStatusPendingPack && (
                <div className="pending-pack-alert">
                  🚚 Carrier already reports <strong>{String(order.shipment.reportedStatusPendingPack).replace(/_/g, ' ')}</strong> — AWB was made before packing. Pack this now.
                </div>
              )}
            </div>

            <div className="order-checklist">
              <div className="checklist-title">ITEMS TO CHECK & PACK:</div>
              {lines.map((line) => (
                <div key={line.id} className="checklist-item">
                  <input type="checkbox" className="item-checkbox" id={`chk-${line.id}`} defaultChecked={true} />
                  <label htmlFor={`chk-${line.id}`} className="item-details">
                    <div className="item-sku">{line.sku}</div>
                    <div className="item-qty">Qty: <strong>{line.netRequiredQty || line.qty || 0}</strong></div>
                  </label>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="submit-pack-btn"
              onClick={handleAwbSubmit}
              disabled={isSubmitting || !awb || isPacked}
            >
              {isSubmitting ? 'MARKING PACKED...' : (isPacked ? 'ALREADY PACKED' : 'CONFIRM & PACK')}
            </button>
          </div>
        );
      })()}

      {/* Recent Activity Log */}
      {recentPacks.length > 0 && (
        <div className="history-section">
          <h3>Recent Session Packs</h3>
          <div className="history-list">
            {recentPacks.map((item, idx) => (
              <div key={idx} className="history-row">
                <div className="row-main">
                  <strong>#{item.orderNumber}</strong> packed via <span className="partner-name">{item.partner.toUpperCase()}</span>
                </div>
                <div className="row-meta">
                  AWB: <code>{item.awb}</code> @ {item.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full screen camera scanner overlay */}
      {activeScanner && (
        <div className="camera-overlay-modal">
          <div className="camera-overlay-header">
            <span>Scanning {activeScanner === 'order' ? 'Order Number' : 'AWB Number'}</span>
            <button className="camera-close-btn" onClick={() => setActiveScanner(null)}>
              ✕ Cancel
            </button>
          </div>
          <div className="camera-viewfinder-container">
            <div id="quick-pack-camera-reader"></div>
            <div className="scanner-laser"></div>
            <div className="viewfinder-guide">
              <div className="guide-corners top-left"></div>
              <div className="guide-corners top-right"></div>
              <div className="guide-corners bottom-left"></div>
              <div className="guide-corners bottom-right"></div>
            </div>
          </div>
          <div className="camera-overlay-footer">
            <p>Align the barcode or QR code within the frame to scan.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuickPackPage;
